import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import config from '../config';
import {
  AdminDataGrid,
  AdminFilterBar,
  AdminPageHeader,
  AdminStatusBadge,
  DataGridColumn,
  DataGridRowAction,
  FilterConfig,
  HeaderAction,
  SortDirection,
} from './components/AdminMasterDetail';
import {
  AdminSectionHeading,
  ConfirmDialog,
  UnsavedChangesIndicator,
} from './components/AdminUiPrimitives';
import AdminAiAssistantModal from './components/AdminAiAssistantModal';
import {
  IconDelete,
  IconDescription,
  IconEdit,
  IconGeneral,
  IconRelationships,
  IconSpark,
} from './components/AdminIcons';
import { useAdminApi } from './hooks/useAdminApi';
import { CategoryDraftPayload, parseCategoryDrafts } from './utils/categoryAiDraft';
import { buildBulkCategoryCreatePayloads, BulkCategoryCreatePayload } from './utils/categoryBulkPayload';
import { getApprovalStatusBadgeTone, getApprovalStatusLabel } from './utils/categoryApproval';

interface Category {
  id: number;
  name: string;
  description: string;
  sortOrder?: number;
  includeInTasks?: boolean;
  detailedDescription?: string;
  tips?: string;
  example?: string;
  tags?: string;
  wordDetailedDescription?: string;
  parentCategoryId?: number | null;
  accessibilityType?: number;
  isEnabled?: boolean;
  approvalStatus?: string;
  courseIds?: number[];
  curriculumContentDescriptionCode?: string;
  curriculumContentDescription?: string;
  curriculumAchievementStandard?: string;
  curriculumElaboration?: string;
  curriculumStrand?: string;
  curriculumSubStrand?: string;
  curriculumLearningAreaCapability?: string;
  curriculumDisciplineLanguage?: string;
  curriculumLevelBand?: string;
  curriculumPathway?: string;
}

interface CategoryListItem {
  id: number;
  name: string;
  description: string;
  parentCategoryId?: number | null;
  sortOrder?: number;
  includeInTasks?: boolean;
  isEnabled?: boolean;
  approvalStatus?: string;
}

interface CourseOption {
  id: number;
  name: string;
  parentCourseId?: number | null;
  parentCourseName?: string | null;
}

interface CourseLeafOption {
  id: number;
  name: string;
  pathLabel: string;
}

type CategoryStatus = 'Active' | 'Not Active';
type SortKey = 'name' | 'parent' | 'status' | 'updated';
type CategoriesViewMode = 'grid' | 'hierarchy';

interface CategoryFormData {
  id: number;
  name: string;
  description: string;
  sortOrder: number;
  detailedDescription: string;
  tips: string;
  example: string;
  tags: string;
  wordDetailedDescription: string;
  includeInTasks: boolean;
  parentCategoryId: number | null;
  accessibilityType: number;
  isEnabled: boolean;
  approvalStatus: string;
  courseIds: number[];
  curriculumContentDescriptionCode: string;
  curriculumContentDescription: string;
  curriculumAchievementStandard: string;
  curriculumElaboration: string;
  curriculumStrand: string;
  curriculumSubStrand: string;
  curriculumLearningAreaCapability: string;
  curriculumDisciplineLanguage: string;
  curriculumLevelBand: string;
  curriculumPathway: string;
}

interface CategoryRow {
  id: number;
  name: string;
  parent: string;
  status: CategoryStatus;
  approvalStatus: string;
  updatedAt: string;
  updatedLabel: string;
  category: Category;
}

interface ConfirmDialogState {
  title: string;
  message: string;
  actions: Array<{
    label: string;
    variant?: 'default' | 'primary' | 'destructive';
    onClick: () => void;
    disabled?: boolean;
  }>;
}

interface CategoryDraftReviewItem extends CategoryDraftPayload {
  id: number;
}

interface CreatedCategoryPayload extends BulkCategoryCreatePayload {}

interface BulkUpdateDraft {
  status: 'no-change' | 'active' | 'not-active';
  approvalStatus: 'no-change' | 'Pending Review' | 'Approved' | 'Manual';
  includeInTasks: 'no-change' | 'true' | 'false';
}

const DEFAULT_PAGE_SIZE = 8;

interface HtmlEditorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  helpText?: string;
}

const HtmlEditorField: React.FC<HtmlEditorFieldProps> = ({ label, value, onChange, placeholder, minHeight = 110, helpText }) => {
  const editorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const syncEditorValue = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleCommand = (command: string, valueArg?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, valueArg);
    syncEditorValue();
  };

  return (
    <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
      {label}
      <div style={{ border: '1px solid #c9d6e8', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 10px', borderBottom: '1px solid #e3ebf5', background: '#fbfdff' }}>
          <button type="button" className="amd-btn amd-btn-ghost" onClick={() => handleCommand('bold')} style={{ padding: '4px 8px', minWidth: 34, fontWeight: 700 }}>B</button>
          <button type="button" className="amd-btn amd-btn-ghost" onClick={() => handleCommand('italic')} style={{ padding: '4px 8px', minWidth: 34, fontStyle: 'italic' }}>I</button>
          <button type="button" className="amd-btn amd-btn-ghost" onClick={() => handleCommand('underline')} style={{ padding: '4px 8px', minWidth: 34, textDecoration: 'underline' }}>U</button>
          <button type="button" className="amd-btn amd-btn-ghost" onClick={() => handleCommand('insertUnorderedList')} style={{ padding: '4px 8px', minWidth: 34 }}>• List</button>
          <button type="button" className="amd-btn amd-btn-ghost" onClick={() => handleCommand('insertOrderedList')} style={{ padding: '4px 8px', minWidth: 34 }}>1. List</button>
        </div>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={syncEditorValue}
          onBlur={syncEditorValue}
          data-placeholder={placeholder}
          style={{ minHeight, padding: '10px 12px', fontSize: '0.9rem', lineHeight: 1.5, outline: 'none', whiteSpace: 'pre-wrap' }}
        />
      </div>
      {helpText && <div style={{ fontSize: '0.78rem', color: '#5f6f89', fontWeight: 500 }}>{helpText}</div>}
    </label>
  );
};

const buildEmptyForm = (): CategoryFormData => ({
  id: 0,
  name: '',
  description: '',
  sortOrder: 1,
  detailedDescription: '',
  tips: '',
  example: '',
  tags: '',
  wordDetailedDescription: '',
  includeInTasks: true,
  parentCategoryId: null,
  accessibilityType: 1,
  isEnabled: true,
  approvalStatus: 'Approved',
  courseIds: [],
  curriculumContentDescriptionCode: '',
  curriculumContentDescription: '',
  curriculumAchievementStandard: '',
  curriculumElaboration: '',
  curriculumStrand: '',
  curriculumSubStrand: '',
  curriculumLearningAreaCapability: '',
  curriculumDisciplineLanguage: '',
  curriculumLevelBand: '',
  curriculumPathway: '',
});

interface SingleCategoryAiResponse {
  DetailedDescription?: string;
  Tips?: string;
  Example?: string;
  WordDetailedDescription?: string;
  Tags?: string;
}

const parseSingleCategoryAiResponse = (generatedText: string): SingleCategoryAiResponse | null => {
  const trimmed = generatedText.trim();
  if (!trimmed) {
    return null;
  }

  const candidates = [trimmed];
  const fencedMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fencedMatch?.[1]) {
    candidates.unshift(fencedMatch[1].trim());
  }

  const firstBrace = trimmed.indexOf('{');
  const lastBrace = trimmed.lastIndexOf('}');
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    candidates.unshift(trimmed.slice(firstBrace, lastBrace + 1));
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate) as SingleCategoryAiResponse;
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch {
      // Try next candidate.
    }
  }

  return null;
};

const toTrimmedString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toCategoryForm = (category: Category): CategoryFormData => ({
  id: category.id,
  name: category.name,
  description: category.description,
  sortOrder: category.sortOrder && category.sortOrder > 0 ? category.sortOrder : 1,
  detailedDescription: category.detailedDescription ?? '',
  tips: category.tips ?? '',
  example: category.example ?? '',
  tags: category.tags ?? '',
  wordDetailedDescription: category.wordDetailedDescription ?? '',
  includeInTasks: category.includeInTasks !== false,
  parentCategoryId: category.parentCategoryId ?? null,
  accessibilityType: category.accessibilityType ?? 1,
  isEnabled: category.isEnabled !== false,
  approvalStatus: category.approvalStatus || 'Approved',
  courseIds: category.courseIds || [],
  curriculumContentDescriptionCode: category.curriculumContentDescriptionCode ?? '',
  curriculumContentDescription: category.curriculumContentDescription ?? '',
  curriculumAchievementStandard: category.curriculumAchievementStandard ?? '',
  curriculumElaboration: category.curriculumElaboration ?? '',
  curriculumStrand: category.curriculumStrand ?? '',
  curriculumSubStrand: category.curriculumSubStrand ?? '',
  curriculumLearningAreaCapability: category.curriculumLearningAreaCapability ?? '',
  curriculumDisciplineLanguage: category.curriculumDisciplineLanguage ?? '',
  curriculumLevelBand: category.curriculumLevelBand ?? '',
  curriculumPathway: category.curriculumPathway ?? '',
});

const ManageCategoriesV2: React.FC = () => {
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [categories, setCategories] = useState<CategoryListItem[]>([]);
  const [categoryLookup, setCategoryLookup] = useState<CategoryListItem[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [courseLeafOptions, setCourseLeafOptions] = useState<CourseLeafOption[]>([]);
  const [coursesLoaded, setCoursesLoaded] = useState(false);
  const [courseLeafOptionsLoaded, setCourseLeafOptionsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [parentFilter, setParentFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('');
  const [viewMode, setViewMode] = useState<CategoriesViewMode>('hierarchy');
  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [detailedDescriptionTab, setDetailedDescriptionTab] = useState<'edit' | 'preview'>('edit');
  const [formData, setFormData] = useState<CategoryFormData>(buildEmptyForm());
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalMode, setAiModalMode] = useState<'single' | 'bulk'>('single');
  const [selectedBulkCourseName, setSelectedBulkCourseName] = useState('');
  const [draftReviewOpen, setDraftReviewOpen] = useState(false);
  const [draftReviewItems, setDraftReviewItems] = useState<CategoryDraftReviewItem[]>([]);
  const [draftReviewLoading, setDraftReviewLoading] = useState(false);
  const [createdBulkCategoryIds, setCreatedBulkCategoryIds] = useState<number[]>([]);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkUpdateSaving, setBulkUpdateSaving] = useState(false);
  const [bulkUpdateDraft, setBulkUpdateDraft] = useState<BulkUpdateDraft>({
    status: 'no-change',
    approvalStatus: 'no-change',
    includeInTasks: 'no-change',
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCategory, setPreviewCategory] = useState<Category | null>(null);
  const [editorTab, setEditorTab] = useState<'details' | 'curriculum'>('details');
  const [hierarchyRoots, setHierarchyRoots] = useState<CategoryListItem[]>([]);
  const [hierarchyChildrenByParent, setHierarchyChildrenByParent] = useState<Record<number, CategoryListItem[]>>({});
  const [expandedHierarchyIds, setExpandedHierarchyIds] = useState<Set<number>>(new Set());
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [loadingHierarchyChildrenIds, setLoadingHierarchyChildrenIds] = useState<Set<number>>(new Set());
  const [hasLoadedGridCategories, setHasLoadedGridCategories] = useState(false);

  const buildSingleAiCurriculumContext = () => {
    const metadataLines = [
      ['Content Description Code', formData.curriculumContentDescriptionCode],
      ['Content Description', formData.curriculumContentDescription],
      ['Strand', formData.curriculumStrand],
      ['Sub-strand', formData.curriculumSubStrand],
      ['Level Band', formData.curriculumLevelBand],
      ['Learning Area / Capability', formData.curriculumLearningAreaCapability],
      ['Discipline / Language', formData.curriculumDisciplineLanguage],
      ['Pathway', formData.curriculumPathway],
      ['Achievement Standard', formData.curriculumAchievementStandard],
      ['Elaboration', formData.curriculumElaboration],
    ]
      .filter(([, value]) => value.trim().length > 0)
      .map(([label, value]) => `${label}: ${value.trim()}`);

    return [
      `Category ID: ${formData.id ?? 'new'}`,
      `Name: ${formData.name || 'New category'}`,
      `Description: ${formData.description || ''}`,
      `Detailed description: ${formData.detailedDescription || ''}`,
      `Tips: ${formData.tips || ''}`,
      `Example: ${formData.example || ''}`,
      `Word detailed description: ${formData.wordDetailedDescription || ''}`,
      `Tags: ${formData.tags || ''}`,
      metadataLines.length > 0 ? `Curriculum metadata:\n${metadataLines.join('\n')}` : 'Curriculum metadata: none provided',
    ].join('\n');
  };

  const API_BASE_URL = `${config.apiBaseUrl}/api`;
  const isNarrowLayout = viewportWidth < 1140;
  const isSmallScreen = viewportWidth < 768;

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const selectedCourseId = useMemo(() => {
    if (!courseFilter.startsWith('course:')) {
      return null;
    }

    const nextCourseId = Number(courseFilter.split(':')[1]);
    return Number.isFinite(nextCourseId) ? nextCourseId : null;
  }, [courseFilter]);

  const buildGridParams = useCallback((options?: { parent?: string; parentCategoryId?: number }) => ({
    courseId: selectedCourseId ?? undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    approval: approvalFilter !== 'all' ? approvalFilter : undefined,
    parent: options?.parent,
    parentCategoryId: options?.parentCategoryId,
  }), [approvalFilter, selectedCourseId, statusFilter]);

  const toCategoryRow = useCallback((category: CategoryListItem): CategoryRow => ({
    id: category.id,
    name: category.name,
    parent: category.parentCategoryId ? 'Child category' : 'Top level',
    status: category.isEnabled === false ? 'Not Active' : 'Active',
    approvalStatus: category.approvalStatus || 'Approved',
    updatedAt: category.id.toString(),
    updatedLabel: 'Updated',
    category: {
      id: category.id,
      name: category.name,
      description: category.description,
      sortOrder: category.sortOrder,
      includeInTasks: category.includeInTasks,
      parentCategoryId: category.parentCategoryId,
      isEnabled: category.isEnabled,
      approvalStatus: category.approvalStatus,
    },
  }), []);

  const fetchCategoryLookup = useCallback(async () => {
    if (!hasActiveOrganization) {
      setCategoryLookup([]);
      return;
    }

    try {
      const token = await getManagementToken();
      const response = await axios.get<CategoryListItem[]>(`${API_BASE_URL}/categories/lookup`, {
        headers: getOrganizationHeaders(token),
      });
      const nextLookup = Array.isArray(response.data) ? response.data : [];
      setCategoryLookup(nextLookup.map((category) => ({
        id: category.id,
        name: category.name,
        description: category.description || '',
        parentCategoryId: category.parentCategoryId,
        sortOrder: category.sortOrder,
        includeInTasks: true,
        isEnabled: true,
        approvalStatus: 'Approved',
      })));
    } catch (err) {
      console.error('Error fetching category lookup:', err);
      setCategoryLookup([]);
    }
  }, [API_BASE_URL, getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  const fetchCategories = useCallback(async () => {
    if (!hasActiveOrganization) {
      setCategories([]);
      setHasLoadedGridCategories(false);
      setError('No organization selected. Please choose an organization first.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await getManagementToken();
      const response = await axios.get<CategoryListItem[]>(`${API_BASE_URL}/categories/grid`, {
        headers: getOrganizationHeaders(token),
        params: buildGridParams({
          parent: parentFilter !== 'all' ? parentFilter : undefined,
        }),
      });

      const nextCategories = Array.isArray(response.data) ? response.data : [];
      setCategories(nextCategories);
      setHasLoadedGridCategories(true);
      if (nextCategories.length === 0) {
        setFormData(buildEmptyForm());
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories.');
      setCategories([]);
      setHasLoadedGridCategories(false);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, buildGridParams, getManagementToken, getOrganizationHeaders, hasActiveOrganization, parentFilter]);

  const fetchHierarchyRoots = useCallback(async () => {
    if (!hasActiveOrganization) {
      setHierarchyRoots([]);
      setHierarchyChildrenByParent({});
      setExpandedHierarchyIds(new Set());
      return;
    }

    try {
      setHierarchyLoading(true);
      setError(null);
      const token = await getManagementToken();
      const response = await axios.get<CategoryListItem[]>(`${API_BASE_URL}/categories/grid`, {
        headers: getOrganizationHeaders(token),
        params: buildGridParams({ parent: 'top-level' }),
      });

      const nextRoots = Array.isArray(response.data) ? response.data : [];
      setHierarchyRoots(nextRoots);
      setHierarchyChildrenByParent({});
      setExpandedHierarchyIds(new Set());
    } catch (err) {
      console.error('Error fetching category hierarchy roots:', err);
      setHierarchyRoots([]);
      setHierarchyChildrenByParent({});
      setError('Failed to load categories.');
    } finally {
      setHierarchyLoading(false);
    }
  }, [API_BASE_URL, buildGridParams, getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  const fetchHierarchyChildren = useCallback(async (parentCategoryId: number) => {
    if (hierarchyChildrenByParent[parentCategoryId]) {
      return;
    }

    try {
      const token = await getManagementToken();
      setLoadingHierarchyChildrenIds((previous) => {
        const next = new Set(previous);
        next.add(parentCategoryId);
        return next;
      });

      const response = await axios.get<CategoryListItem[]>(`${API_BASE_URL}/categories/grid`, {
        headers: getOrganizationHeaders(token),
        params: buildGridParams({ parentCategoryId }),
      });

      const nextChildren = Array.isArray(response.data) ? response.data : [];
      setHierarchyChildrenByParent((previous) => ({ ...previous, [parentCategoryId]: nextChildren }));
    } catch (err) {
      console.error('Error fetching category children:', err);
      setHierarchyChildrenByParent((previous) => ({ ...previous, [parentCategoryId]: [] }));
    } finally {
      setLoadingHierarchyChildrenIds((previous) => {
        const next = new Set(previous);
        next.delete(parentCategoryId);
        return next;
      });
    }
  }, [API_BASE_URL, buildGridParams, getManagementToken, getOrganizationHeaders, hierarchyChildrenByParent]);

  const fetchCourses = useCallback(async () => {
    if (!hasActiveOrganization || coursesLoaded) {
      return;
    }

    try {
      const token = await getManagementToken();
      const response = await axios.get<CourseOption[]>(`${API_BASE_URL}/courses`, {
        headers: getOrganizationHeaders(token),
      });

      const nextCourses = Array.isArray(response.data) ? response.data : [];
      setCourses(nextCourses);
      setCoursesLoaded(true);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setCourses([]);
      setCoursesLoaded(true);
    }
  }, [API_BASE_URL, coursesLoaded, getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  const fetchCourseLeafOptions = useCallback(async () => {
    if (!hasActiveOrganization || courseLeafOptionsLoaded) {
      return;
    }

    try {
      const token = await getManagementToken();
      const response = await axios.get<CourseLeafOption[]>(`${API_BASE_URL}/courses/leaf-options`, {
        headers: getOrganizationHeaders(token),
      });

      const nextLeafOptions = Array.isArray(response.data) ? response.data : [];
      setCourseLeafOptions(nextLeafOptions);
      setCourseFilter((previous) => {
        if (previous) {
          return previous;
        }

        return nextLeafOptions[0] ? `course:${nextLeafOptions[0].id}` : 'all';
      });
      setCourseLeafOptionsLoaded(true);
    } catch (err) {
      console.error('Error fetching leaf course options:', err);
      setCourseLeafOptions([]);
      setCourseFilter((previous) => previous || 'all');
      setCourseLeafOptionsLoaded(true);
    }
  }, [API_BASE_URL, courseLeafOptionsLoaded, getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  const loadCategoryDetails = useCallback(async (categoryId: number) => {
    if (!hasActiveOrganization) {
      setSelectedCategory(null);
      return;
    }

    setLoadingDetails(true);
    setError(null);

    try {
      const token = await getManagementToken();
      const response = await axios.get<Category>(`${API_BASE_URL}/categories/${categoryId}/details`, {
        headers: getOrganizationHeaders(token),
      });

      const detail = response.data;
      setSelectedCategory(detail);
      setFormData(toCategoryForm(detail));
      await fetchCourses();
    } catch (err) {
      console.error('Error loading category details:', err);
      setSelectedCategory(null);
      setFormData(buildEmptyForm());
      setError('Failed to load category details.');
    } finally {
      setLoadingDetails(false);
    }
  }, [API_BASE_URL, fetchCourses, getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  const getCategoryDetailsById = useCallback(async (categoryId: number): Promise<Category> => {
    const token = await getManagementToken();
    const response = await axios.get<Category>(`${API_BASE_URL}/categories/${categoryId}/details`, {
      headers: getOrganizationHeaders(token),
    });
    return response.data;
  }, [API_BASE_URL, getManagementToken, getOrganizationHeaders]);

  useEffect(() => {
    if (!courseFilter || viewMode !== 'grid') {
      return;
    }

    void fetchCategories();
  }, [courseFilter, fetchCategories, viewMode]);

  useEffect(() => {
    void fetchCourses();
    void fetchCourseLeafOptions();
  }, [fetchCourseLeafOptions, fetchCourses]);

  useEffect(() => {
    void fetchCategoryLookup();
  }, [fetchCategoryLookup]);

  useEffect(() => {
    if (!courseFilter || viewMode !== 'hierarchy') {
      return;
    }

    void fetchHierarchyRoots();
  }, [courseFilter, fetchHierarchyRoots, viewMode]);

  useEffect(() => {
    if (createdBulkCategoryIds.length === 0) {
      return;
    }

    const latestCreatedId = createdBulkCategoryIds[createdBulkCategoryIds.length - 1];
    if (latestCreatedId) {
      setSelectedCategoryId(latestCreatedId);
      setIsAdding(false);
      setSelectedCategory(null);
      setMessage('New categories were created and loaded for review.');
    }
  }, [createdBulkCategoryIds]);

  useEffect(() => {
    setPage(1);
  }, [searchValue, statusFilter, approvalFilter, parentFilter, courseFilter]);

  const rows = useMemo<CategoryRow[]>(() => categories.map((category) => toCategoryRow(category)), [categories, toCategoryRow]);

  const parentFilterOptions = useMemo(() => {
    return [
      { value: 'all', label: 'All categories' },
      { value: 'top-level', label: 'Top level' },
      { value: 'child', label: 'Child categories' },
    ];
  }, []);

  const filteredRows = useMemo(() => {
    const nextRows = rows.filter((row) => {
      const searchable = `${row.name} ${row.parent}`.toLowerCase();
      const matchesSearch = searchValue.trim() === '' || searchable.includes(searchValue.trim().toLowerCase());

      return matchesSearch;
    });

    return [...nextRows].sort((first, second) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      if (sortKey === 'name') {
        return first.name.localeCompare(second.name) * direction;
      }
      if (sortKey === 'parent') {
        return first.parent.localeCompare(second.parent) * direction;
      }
      if (sortKey === 'status') {
        return first.status.localeCompare(second.status) * direction;
      }
      return first.id - second.id;
    });
  }, [rows, searchValue, sortDirection, sortKey]);

  const hierarchyRootRows = useMemo(() => {
    const searchTerm = searchValue.trim().toLowerCase();

    return hierarchyRoots
      .filter((category) => {
        if (!searchTerm) {
          return true;
        }

        const searchable = `${category.name} ${category.description}`.toLowerCase();
        return searchable.includes(searchTerm);
      })
      .sort((first, second) => {
        const firstOrder = first.sortOrder && first.sortOrder > 0 ? first.sortOrder : 1;
        const secondOrder = second.sortOrder && second.sortOrder > 0 ? second.sortOrder : 1;
        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }
        return first.name.localeCompare(second.name);
      })
      .map((category) => toCategoryRow(category));
  }, [hierarchyRoots, searchValue, toCategoryRow]);

  const toggleHierarchyCategory = (categoryId: number) => {
    setExpandedHierarchyIds((previous) => {
      const next = new Set(previous);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const courseFilterOptions = useMemo(() => {
    return [
      { value: 'all', label: 'All courses' },
      ...courseLeafOptions.map((course) => ({
        value: `course:${course.id}`,
        label: course.pathLabel,
      })),
    ];
  }, [courseLeafOptions]);

  const courseLinkLabelById = useMemo(() => {
    const labels = new Map<number, string>();
    const coursesById = new Map<number, CourseOption>();

    courses.forEach((course) => {
      coursesById.set(course.id, course);
    });

    const buildCoursePathLabel = (courseId: number): string => {
      const segments: string[] = [];
      const visited = new Set<number>();
      let current = coursesById.get(courseId);

      while (current && visited.add(current.id)) {
        const courseName = current.name.trim();
        if (courseName) {
          segments.push(courseName);
        }

        if (!current.parentCourseId) {
          break;
        }

        current = coursesById.get(current.parentCourseId);
      }

      segments.reverse();
      return segments.length > 0 ? segments.join(' > ') : `Course ${courseId}`;
    };

    courses.forEach((course) => {
      labels.set(course.id, buildCoursePathLabel(course.id));
    });

    courseLeafOptions.forEach((course) => {
      if (!labels.has(course.id)) {
        labels.set(course.id, course.pathLabel || course.name);
      }
    });

    return labels;
  }, [courseLeafOptions, courses]);

  const pagedRows = useMemo(() => {
    const startIndex = (page - 1) * DEFAULT_PAGE_SIZE;
    return filteredRows.slice(startIndex, startIndex + DEFAULT_PAGE_SIZE);
  }, [filteredRows, page]);

  const headerActions: HeaderAction[] = [
    {
      key: 'bulk-update',
      label: `Bulk Update (${selectedIds.length})`,
      variant: 'ghost',
      disabled: selectedIds.length === 0,
      onClick: () => {
        setBulkUpdateOpen(true);
        setError(null);
        setMessage(null);
      },
    },
    {
      key: 'bulk-ai-assist',
      label: 'AI Assist (bulk)',
      variant: 'secondary',
      onClick: () => {
        setAiModalMode('bulk');
        setAiModalOpen(true);
      },
    },
    {
      key: 'add-category',
      label: 'Add Category',
      variant: 'primary',
      onClick: () => {
        setIsAdding(true);
        setSelectedCategoryId(null);
        setSelectedCategory(null);
        setFormData(buildEmptyForm());
        setEditorTab('details');
        setMessage(null);
      },
    },
  ];

  const filters: FilterConfig[] = useMemo(() => {
    const nextFilters: FilterConfig[] = [
      {
        key: 'status',
        label: 'Status',
        value: statusFilter,
        options: [
          { value: 'all', label: 'All statuses' },
          { value: 'active', label: 'Enabled' },
          { value: 'not active', label: 'Not Active' },
        ],
        onChange: setStatusFilter,
      },
      {
        key: 'approval',
        label: 'Approval',
        value: approvalFilter,
        options: [
          { value: 'all', label: 'All approvals' },
          { value: 'pending review', label: 'Pending Review' },
          { value: 'approved', label: 'Approved' },
          { value: 'manual', label: 'Manual' },
        ],
        onChange: setApprovalFilter,
      },
      {
        key: 'course',
        label: 'Course',
        value: courseFilter,
        options: courseFilterOptions,
        onChange: setCourseFilter,
      },
    ];

    if (viewMode === 'grid') {
      nextFilters.splice(1, 0, {
        key: 'parent',
        label: 'Parent',
        value: parentFilter,
        options: parentFilterOptions,
        onChange: setParentFilter,
      });
    }

    return nextFilters;
  }, [approvalFilter, courseFilter, courseFilterOptions, parentFilter, parentFilterOptions, statusFilter, viewMode]);

  const columns: DataGridColumn<CategoryRow>[] = [
    {
      key: 'name',
      label: 'Category',
      sortable: true,
      width: '34%',
      render: (row) => (
        <div>
          <div style={{ fontWeight: 700, color: '#152746' }}>{row.name}</div>
          <div style={{ marginTop: 4, fontSize: '0.8rem', color: '#5f6f89' }}>{row.category.description || 'No description provided'}</div>
        </div>
      ),
    },
    {
      key: 'parent',
      label: 'Parent',
      sortable: true,
      width: '22%',
      render: (row) => <span>{row.parent}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: '12%',
      render: (row) => {
        if (row.status === 'Not Active') {
          return <span className="amd-badge amd-badge-archived">Not Active</span>;
        }

        return <AdminStatusBadge status="Active" />;
      },
    },
    {
      key: 'approval',
      label: 'Approval',
      sortable: true,
      width: '16%',
      render: (row) => {
        const label = getApprovalStatusLabel(row.approvalStatus);
        const tone = getApprovalStatusBadgeTone(row.approvalStatus);
        const background = tone === 'success' ? '#ecfdf3' : tone === 'danger' ? '#fef2f2' : '#f3f4f6';
        const color = tone === 'success' ? '#166534' : tone === 'danger' ? '#b91c1c' : '#374151';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 8px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700, background, color }}>
            {label}
          </span>
        );
      },
    },
    {
      key: 'updated',
      label: 'Updated',
      sortable: true,
      width: '16%',
      render: (row) => <span>{row.updatedLabel}</span>,
    },
  ];

  const rowActions: DataGridRowAction<CategoryRow>[] = [
    {
      key: 'preview',
      label: 'Preview',
      icon: <IconDescription size={14} />,
      onClick: async (row) => {
        setPreviewOpen(true);
        setPreviewLoading(true);
        setPreviewCategory(null);
        try {
          const detail = await getCategoryDetailsById(row.id);
          setPreviewCategory(detail);
        } catch {
          setError('Failed to load preview content.');
          setPreviewOpen(false);
        } finally {
          setPreviewLoading(false);
        }
      },
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: <IconEdit size={14} />,
      onClick: async (row) => {
        setIsAdding(false);
        setSelectedCategoryId(row.id);
        setSelectedCategory(null);
        setEditorTab('details');
        setMessage(null);
        await loadCategoryDetails(row.id);
      },
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <IconDelete size={14} />,
      onClick: (row) => {
        setConfirmDialog({
          title: 'Delete category',
          message: `Delete “${row.name}”? This action cannot be undone.`,
          actions: [
            {
              label: 'Cancel',
              variant: 'default',
              onClick: () => setConfirmDialog(null),
            },
            {
              label: 'Delete',
              variant: 'destructive',
              onClick: async () => {
                setConfirmDialog(null);
                try {
                  const token = await getManagementToken();
                  const response = await axios.delete(`${API_BASE_URL}/categories/${row.id}`, {
                    headers: getOrganizationHeaders(token),
                  });

                  if (response.status < 200 || response.status >= 300) {
                    throw new Error('Failed to delete category');
                  }

                  await fetchCategories();
                  await fetchHierarchyRoots();
                  setMessage('Category deleted.');
                } catch (err) {
                  console.error(err);
                  setError('Failed to delete category.');
                }
              },
            },
          ],
        });
      },
      tone: 'destructive',
    },
  ];

  const handleOpenPreviewForSelected = async () => {
    if (!selectedCategoryId) {
      return;
    }

    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewCategory(null);
    try {
      const detail = await getCategoryDetailsById(selectedCategoryId);
      setPreviewCategory(detail);
    } catch {
      setError('Failed to load preview content.');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedIds.length === 0) {
      setBulkUpdateOpen(false);
      return;
    }

    const hasNoChanges =
      bulkUpdateDraft.status === 'no-change' &&
      bulkUpdateDraft.approvalStatus === 'no-change' &&
      bulkUpdateDraft.includeInTasks === 'no-change';

    if (hasNoChanges) {
      setError('Select at least one field to update in bulk.');
      return;
    }

    try {
      setBulkUpdateSaving(true);
      setError(null);
      setMessage(null);
      const token = await getManagementToken();
      const headers = {
        'Content-Type': 'application/json',
        ...getOrganizationHeaders(token),
      };

      for (const categoryId of selectedIds) {
        const detail = await getCategoryDetailsById(categoryId);
        const basePayload = toCategoryForm(detail);
        const updatedPayload: CategoryFormData = {
          ...basePayload,
          isEnabled: bulkUpdateDraft.status === 'no-change'
            ? basePayload.isEnabled
            : bulkUpdateDraft.status === 'active',
          approvalStatus: bulkUpdateDraft.approvalStatus === 'no-change'
            ? basePayload.approvalStatus
            : bulkUpdateDraft.approvalStatus,
          includeInTasks: bulkUpdateDraft.includeInTasks === 'no-change'
            ? basePayload.includeInTasks
            : bulkUpdateDraft.includeInTasks === 'true',
        };

        await axios.put(`${API_BASE_URL}/categories/${categoryId}`, updatedPayload, { headers });
      }

      await fetchCategories();
      await fetchHierarchyRoots();
      if (selectedCategoryId) {
        await loadCategoryDetails(selectedCategoryId);
      }
      setMessage(`Updated ${selectedIds.length} categories.`);
      setBulkUpdateOpen(false);
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
      setError('Bulk update failed. Please try again.');
    } finally {
      setBulkUpdateSaving(false);
    }
  };

  const handleEditSelection = async (category: Category) => {
    if (loadingDetails) {
      return;
    }

    setIsAdding(false);
    setSelectedCategoryId(category.id);
    setSelectedCategory(null);
    setEditorTab('details');
    setMessage(null);
    await loadCategoryDetails(category.id);
  };

  const handleCourseToggle = (courseId: number) => {
    setFormData((previous) => ({
      ...previous,
      courseIds: previous.courseIds.includes(courseId)
        ? previous.courseIds.filter((item) => item !== courseId)
        : [...previous.courseIds, courseId],
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Category name is required.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const token = await getManagementToken();
      const headers = {
        'Content-Type': 'application/json',
        ...getOrganizationHeaders(token),
      };
      const request = isAdding
        ? axios.post<Category>(`${API_BASE_URL}/categories`, formData, { headers })
        : axios.put<Category>(`${API_BASE_URL}/categories/${formData.id}`, formData, { headers });

      const response = await request;
      const savedCategory = response.data;

      await fetchCategories();
      await fetchHierarchyRoots();
      setMessage(isAdding ? 'Category created.' : 'Category updated.');
      setIsAdding(false);
      const savedCategoryId = savedCategory?.id ?? formData.id;
      setSelectedCategoryId(savedCategoryId);
      if (savedCategoryId) {
        await loadCategoryDetails(savedCategoryId);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to save category.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setSelectedCategoryId(selectedCategory?.id ?? null);
    setFormData(selectedCategory ? toCategoryForm(selectedCategory) : buildEmptyForm());
    setMessage(null);
  };

  const handleGenerateCategoryDrafts = async (generatedText: string) => {
    const parsedDrafts = parseCategoryDrafts(generatedText);
    if (parsedDrafts.length === 0) {
      setError('The assistant response could not be parsed into category drafts.');
      return;
    }

    const nextDrafts = parsedDrafts.map((draft, index) => ({
      id: index + 1,
      name: draft.name || `Category ${index + 1}`,
      description: draft.description || 'Add a short description',
      detailedDescription: draft.detailedDescription || '',
      tips: draft.tips || '',
      example: draft.example || '',
      wordDetailedDescription: draft.wordDetailedDescription || '',
      tags: draft.tags || '',
      sortOrder: draft.sortOrder ?? index + 1,
      includeInTasks: false,
      accessibilityType: 1,
      isEnabled: false,
      approvalStatus: draft.approvalStatus || 'Pending Review',
    }));

    setDraftReviewItems(nextDrafts);
    setDraftReviewOpen(true);
    setMessage('AI draft prepared for review.');
    setError(null);
  };

  const handleApplyDrafts = async () => {
    if (draftReviewItems.length === 0) {
      return;
    }

    try {
      setDraftReviewLoading(true);
      setError(null);
      const token = await getManagementToken();
      const headers = {
        'Content-Type': 'application/json',
        ...getOrganizationHeaders(token),
      };

      const payloads = buildBulkCategoryCreatePayloads(draftReviewItems);
      const response = await axios.post<Category[]>(`${API_BASE_URL}/categories/bulk`, payloads, { headers });

      const createdCategoryIds = (response.data ?? [])
        .map((item) => item?.id)
        .filter((id): id is number => Boolean(id));

      await fetchCategories();
      await fetchHierarchyRoots();
      setCreatedBulkCategoryIds(createdCategoryIds);
      setDraftReviewOpen(false);
      setDraftReviewItems([]);
      setAiModalOpen(false);
      setMessage(`Created ${createdCategoryIds.length} categories. They are currently disabled for review.`);
      if (createdCategoryIds[0]) {
        await loadCategoryDetails(createdCategoryIds[0]);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to apply AI category drafts.');
    } finally {
      setDraftReviewLoading(false);
    }
  };

  const getAllCategories = (): CategoryListItem[] => {
    return [...categoryLookup].sort((first, second) => {
      const firstOrder = first.sortOrder && first.sortOrder > 0 ? first.sortOrder : 1;
      const secondOrder = second.sortOrder && second.sortOrder > 0 ? second.sortOrder : 1;
      if (firstOrder !== secondOrder) {
        return firstOrder - secondOrder;
      }
      return first.name.localeCompare(second.name);
    });
  };

  const renderHierarchyNode = (row: CategoryRow, depth = 0): React.ReactNode => {
    const expanded = expandedHierarchyIds.has(row.id);
    const isLoadingChildren = loadingHierarchyChildrenIds.has(row.id);
    const hasLoadedChildren = Object.prototype.hasOwnProperty.call(hierarchyChildrenByParent, row.id);
    const childRows = (hierarchyChildrenByParent[row.id] || [])
      .filter((category) => {
        const searchTerm = searchValue.trim().toLowerCase();
        if (!searchTerm) {
          return true;
        }

        const searchable = `${category.name} ${category.description}`.toLowerCase();
        return searchable.includes(searchTerm);
      })
      .map((category) => toCategoryRow(category));

    return (
      <div key={`${depth}-${row.id}`} style={{ display: 'grid', gap: 4 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            border: selectedCategoryId === row.id ? '1px solid #9fc0ff' : '1px solid #e6edf9',
            background: selectedCategoryId === row.id ? '#edf4ff' : '#fff',
            borderRadius: 8,
            padding: '8px 10px',
            marginLeft: depth * 16,
          }}
        >
          <button
            type="button"
            onClick={() => {
              const opening = !expanded;
              toggleHierarchyCategory(row.id);
              if (opening && !hasLoadedChildren) {
                void fetchHierarchyChildren(row.id);
              }
            }}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#173159',
              fontWeight: 700,
              fontSize: '0.9rem',
              padding: 0,
              minWidth: 18,
            }}
            aria-label={expanded ? `Collapse ${row.name}` : `Expand ${row.name}`}
          >
            {expanded ? '▼' : '▶'}
          </button>

          <button
            type="button"
            onClick={() => void handleEditSelection(row.category)}
            style={{
              border: 'none',
              background: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'grid',
              gap: 2,
              width: '100%',
              padding: 0,
            }}
          >
            <span style={{ fontWeight: 700, color: '#152746' }}>{row.name}</span>
            <span style={{ fontSize: '0.78rem', color: '#5f6f89' }}>
              {row.status} • {getApprovalStatusLabel(row.approvalStatus)}
            </span>
          </button>
        </div>

        {expanded && (
          <div style={{ display: 'grid', gap: 4 }}>
            {isLoadingChildren && (
              <div style={{ padding: '2px 8px', marginLeft: (depth + 1) * 16, color: '#607399', fontSize: '0.8rem' }}>
                Loading children...
              </div>
            )}

            {!isLoadingChildren && hasLoadedChildren && childRows.length === 0 && (
              <div style={{ padding: '2px 8px', marginLeft: (depth + 1) * 16, color: '#607399', fontSize: '0.8rem' }}>
                No direct children.
              </div>
            )}

            {!isLoadingChildren && childRows.map((childRow) => renderHierarchyNode(childRow, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const detailTitle = isAdding ? 'Add category' : (selectedCategory?.name ? `Edit ${selectedCategory.name}` : 'Category details');

  return (
    <div style={{ maxWidth: 1500, margin: '0 auto', padding: isSmallScreen ? '16px 12px' : '24px 20px', display: 'grid', gap: 16 }}>
      <AdminPageHeader
        title="Manage Categories"
        subtitle="Create, organize, and manage category structure for the current organization."
        actions={headerActions}
      />

      {error && (
        <div style={{ borderRadius: 10, padding: '12px 14px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          {error}
        </div>
      )}

      {message && (
        <div style={{ borderRadius: 10, padding: '12px 14px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
          {message}
        </div>
      )}

      <AdminFilterBar
        searchValue={searchValue}
        searchPlaceholder="Search categories"
        onSearchChange={setSearchValue}
        filters={filters}
      />

      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: '1px solid #d8e3f6',
          borderRadius: 11,
          background: '#f6f9ff',
          padding: 4,
          width: 'fit-content',
        }}
      >
        <button
          type="button"
          className={`amd-btn ${viewMode === 'hierarchy' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
          onClick={() => setViewMode('hierarchy')}
        >
          Hierarchy View
        </button>
        <button
          type="button"
          className={`amd-btn ${viewMode === 'grid' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
          onClick={() => setViewMode('grid')}
        >
          Grid View
        </button>
      </div>

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: isNarrowLayout ? '1fr' : '1.15fr 0.85fr', alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: '0.9rem', color: '#51627c', fontWeight: 600 }}>
              {viewMode === 'grid' ? filteredRows.length : hierarchyRootRows.length} matching categories
            </div>
            <div style={{ fontSize: '0.85rem', color: '#51627c' }}>
              <UnsavedChangesIndicator dirty={Boolean(isAdding || selectedCategory)} />
            </div>
          </div>

          <div style={{ position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(248, 251, 255, 0.72)',
                borderRadius: 12,
                zIndex: 2,
                pointerEvents: loadingDetails ? 'auto' : 'none',
                opacity: loadingDetails ? 1 : 0,
                transition: 'opacity 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ fontSize: '0.9rem', color: '#35568a', fontWeight: 700, background: '#fff', borderRadius: 999, padding: '8px 12px', boxShadow: '0 4px 12px rgba(21, 39, 70, 0.08)' }}>
                Loading details…
              </div>
            </div>
            {viewMode === 'grid' ? (
              <AdminDataGrid<CategoryRow>
                rows={pagedRows}
                columns={columns}
                selectedRowId={selectedCategoryId ?? null}
                onRowClick={(row) => handleEditSelection(row.category)}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
                showSelection
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={(columnKey) => {
                  if (sortKey === columnKey) {
                    setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
                  } else {
                    setSortKey(columnKey as SortKey);
                    setSortDirection('asc');
                  }
                }}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                totalCount={filteredRows.length}
                onPageChange={setPage}
                emptyMessage="No categories match the current filters."
                emptyActionLabel="Add category"
                onEmptyAction={() => {
                  setIsAdding(true);
                  setSelectedCategoryId(null);
                  setSelectedCategory(null);
                  setFormData(buildEmptyForm());
                  setEditorTab('details');
                }}
                rowActions={rowActions}
                alwaysShowRowActions
              />
            ) : (hierarchyLoading ? (
              <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, padding: 22, background: '#fff', color: '#35568a', fontWeight: 700 }}>
                Loading categories...
              </div>
            ) : (
              <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', padding: 8, display: 'grid', gap: 6 }}>
                {hierarchyRootRows.length === 0 ? (
                  <div style={{ padding: 16, color: '#5f6f89', fontSize: '0.9rem' }}>No categories match the current filters.</div>
                ) : (
                  hierarchyRootRows.map((row) => renderHierarchyNode(row, 0))
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ border: '1px solid #dae4f6', borderRadius: 14, padding: 16, background: '#f8fbff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', color: '#152746' }}>{detailTitle}</h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.84rem', color: '#5f6f89' }}>
                  {isAdding ? 'Create a category and assign it to a parent or course set.' : 'Adjust category details, placement, and visibility.'}
                </p>
              </div>
              {selectedCategory && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ padding: '6px 10px', borderRadius: 999, background: '#e8f1ff', color: '#1f56d9', fontSize: '0.78rem', fontWeight: 700 }}>
                    {selectedCategory.name}
                  </div>
                  <button type="button" className="amd-btn amd-btn-ghost" onClick={() => void handleOpenPreviewForSelected()}>
                    Preview
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={{ border: '1px solid #dfe8f8', borderRadius: 14, background: '#fff', padding: 16, display: 'grid', gap: 16 }}>
            {loadingDetails && !isAdding && selectedCategoryId ? (
              <div style={{ minHeight: 240, border: '1px dashed #c9d6ef', borderRadius: 12, background: '#f8fbff', display: 'grid', placeContent: 'center', textAlign: 'center', color: '#4f658f', gap: 8, padding: 16 }}>
                <div style={{ fontWeight: 700 }}>Loading category details…</div>
                <div style={{ fontSize: '0.82rem' }}>Please wait while the full details are loaded.</div>
              </div>
            ) : !selectedCategory && !isAdding ? (
              <div style={{ minHeight: 260, border: '1px dashed #c9d6ef', borderRadius: 12, background: '#f8fbff', display: 'grid', placeContent: 'center', textAlign: 'center', color: '#4f658f', gap: 8, padding: 16 }}>
                <div style={{ color: '#35568a', display: 'inline-flex', justifySelf: 'center' }}><IconGeneral size={26} /></div>
                <div style={{ fontWeight: 700 }}>Select a category from the list</div>
                <div style={{ fontSize: '0.82rem' }}>or</div>
                <button type="button" className="amd-btn amd-btn-primary" onClick={() => {
                  setIsAdding(true);
                  setSelectedCategoryId(null);
                  setSelectedCategory(null);
                  setFormData(buildEmptyForm());
                  setEditorTab('details');
                  setMessage(null);
                }}>
                  Create a new category
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className={editorTab === 'details' ? 'amd-btn amd-btn-primary' : 'amd-btn amd-btn-ghost'}
                      onClick={() => setEditorTab('details')}
                    >
                      Category Details
                    </button>
                    <button
                      type="button"
                      className={editorTab === 'curriculum' ? 'amd-btn amd-btn-primary' : 'amd-btn amd-btn-ghost'}
                      onClick={() => setEditorTab('curriculum')}
                    >
                      Curriculum Metadata
                    </button>
                  </div>
                  <button type="button" className="amd-btn amd-btn-primary" onClick={() => {
                    setAiModalMode('single');
                    setSelectedBulkCourseName('');
                    setAiModalOpen(true);
                  }}>
                    AI Assist (single)
                  </button>
                </div>

                <div style={{ display: editorTab === 'details' ? 'grid' : 'none', gap: 12 }}>
                  <AdminSectionHeading icon={<IconGeneral size={14} />} title="General" />
                  <div style={{ display: 'grid', gap: 12 }}>
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                      Category name
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(event) => setFormData((previous) => ({ ...previous, name: event.target.value }))}
                        placeholder="Category name"
                        style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem' }}
                      />
                    </label>
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                      Short description
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(event) => setFormData((previous) => ({ ...previous, description: event.target.value }))}
                        placeholder="Short description"
                        style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem' }}
                      />
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr', gap: 12 }}>
                      <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                        Order
                        <input
                          type="number"
                          min={1}
                          value={formData.sortOrder || 1}
                          onChange={(event) => setFormData((previous) => ({ ...previous, sortOrder: Math.max(1, parseInt(event.target.value || '1', 10)) }))}
                          style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem' }}
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                        Accessibility
                        <select
                          value={formData.accessibilityType || 1}
                          onChange={(event) => setFormData((previous) => ({ ...previous, accessibilityType: parseInt(event.target.value, 10) }))}
                          style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', background: '#fff' }}
                        >
                          <option value={1}>Free</option>
                          <option value={2}>Bronze</option>
                          <option value={3}>Silver</option>
                          <option value={4}>Gold</option>
                        </select>
                      </label>
                    </div>
                  </div>
                </div>

                <div style={{ display: editorTab === 'details' ? 'grid' : 'none', gap: 12 }}>
                  <AdminSectionHeading icon={<IconRelationships size={14} />} title="Relationships" />
                  <div style={{ display: 'grid', gap: 12 }}>
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                      Parent category
                      <select
                        value={formData.parentCategoryId ?? ''}
                        onChange={(event) => setFormData((previous) => ({ ...previous, parentCategoryId: event.target.value ? parseInt(event.target.value, 10) : null }))}
                        style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', background: '#fff' }}
                      >
                        <option value="">Top level</option>
                        {getAllCategories()
                          .filter((category) => category.id !== formData.id)
                          .map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                      </select>
                    </label>
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                      In questions
                      <select
                        value={formData.includeInTasks ? 'true' : 'false'}
                        onChange={(event) => setFormData((previous) => ({ ...previous, includeInTasks: event.target.value === 'true' }))}
                        style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', background: '#fff' }}
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </label>
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                      Approval status
                      <select
                        value={formData.approvalStatus || 'Approved'}
                        onChange={(event) => setFormData((previous) => ({ ...previous, approvalStatus: event.target.value }))}
                        style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', background: '#fff' }}
                      >
                        <option value="Pending Review">Pending Review</option>
                        <option value="Approved">Approved</option>
                        <option value="Manual">Manual</option>
                      </select>
                    </label>
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                      Status
                      <select
                        value={formData.isEnabled ? 'true' : 'false'}
                        onChange={(event) => setFormData((previous) => ({ ...previous, isEnabled: event.target.value === 'true' }))}
                        style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', background: '#fff' }}
                      >
                        <option value="true">Enabled</option>
                        <option value="false">Disabled</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div style={{ display: editorTab === 'details' ? 'grid' : 'none', gap: 12 }}>
                  <AdminSectionHeading icon={<IconDescription size={14} />} title="Content" />
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={() => setDetailedDescriptionTab('edit')} className="amd-btn amd-btn-ghost" style={{ flex: 1 }}>Edit details</button>
                      <button type="button" onClick={() => setDetailedDescriptionTab('preview')} className="amd-btn amd-btn-secondary" style={{ flex: 1 }}>Preview</button>
                    </div>
                    {detailedDescriptionTab === 'edit' ? (
                      <HtmlEditorField
                        label="Detailed description"
                        value={formData.detailedDescription}
                        onChange={(value) => setFormData((previous) => ({ ...previous, detailedDescription: value }))}
                        placeholder="Add HTML-rich detailed description"
                        helpText="Use simple HTML like <p>, <strong>, <em>, and lists."
                      />
                    ) : (
                      <div style={{ border: '1px solid #e3ebf5', borderRadius: 10, padding: 10, minHeight: 110, background: '#fbfdff' }} dangerouslySetInnerHTML={{ __html: formData.detailedDescription || '<p style="color:#73829c">No content yet.</p>' }} />
                    )}

                    <div style={{ display: 'grid', gap: 10 }}>
                      <HtmlEditorField
                        label="Tips"
                        value={formData.tips}
                        onChange={(value) => setFormData((previous) => ({ ...previous, tips: value }))}
                        placeholder="Add learner tips"
                        minHeight={90}
                      />

                      <HtmlEditorField
                        label="Example"
                        value={formData.example}
                        onChange={(value) => setFormData((previous) => ({ ...previous, example: value }))}
                        placeholder="Add a worked example"
                        minHeight={90}
                      />

                      <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                        Tags
                        <input
                          type="text"
                          value={formData.tags}
                          onChange={(event) => setFormData((previous) => ({ ...previous, tags: event.target.value }))}
                          placeholder="Comma-separated tags"
                          style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem' }}
                        />
                      </label>

                      <HtmlEditorField
                        label="Word detailed description"
                        value={formData.wordDetailedDescription}
                        onChange={(value) => setFormData((previous) => ({ ...previous, wordDetailedDescription: value }))}
                        placeholder="Add book-ready detailed description"
                        minHeight={110}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ display: editorTab === 'details' ? 'grid' : 'none', gap: 12 }}>
                  <AdminSectionHeading icon={<IconSpark size={14} />} title="Course links" />
                  <div style={{ display: 'grid', gap: 8, maxHeight: 180, overflowY: 'auto', border: '1px solid #e3ebf5', borderRadius: 10, padding: 8 }}>
                    {courses.length === 0 ? (
                      <div style={{ color: '#5f6f89', fontSize: '0.84rem' }}>No courses available.</div>
                    ) : courses.map((course) => (
                      <label key={course.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', color: '#243b69' }}>
                        <input
                          type="checkbox"
                          checked={formData.courseIds.includes(course.id)}
                          onChange={() => handleCourseToggle(course.id)}
                        />
                        <span>{courseLinkLabelById.get(course.id) || course.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ display: editorTab === 'curriculum' ? 'grid' : 'none', gap: 12 }}>
                  <AdminSectionHeading icon={<IconDescription size={14} />} title="Curriculum Metadata" />
                  <div style={{ fontSize: '0.8rem', color: '#5f6f89' }}>
                    Edit the core curriculum fields for this category. These fields can affect category labels in curriculum views.
                  </div>
                  <div style={{ display: 'grid', gap: 12 }}>
                    <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                      Content Description Code
                      <input
                        type="text"
                        value={formData.curriculumContentDescriptionCode}
                        onChange={(event) => setFormData((previous) => ({ ...previous, curriculumContentDescriptionCode: event.target.value }))}
                        placeholder="e.g. VCMNA134"
                        style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem' }}
                      />
                    </label>

                    <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                      Content Description
                      <textarea
                        value={formData.curriculumContentDescription}
                        onChange={(event) => setFormData((previous) => ({ ...previous, curriculumContentDescription: event.target.value }))}
                        placeholder="Curriculum content description"
                        rows={3}
                        style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', resize: 'vertical' }}
                      />
                    </label>

                    <div style={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr', gap: 12 }}>
                      <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                        Strand
                        <input
                          type="text"
                          value={formData.curriculumStrand}
                          onChange={(event) => setFormData((previous) => ({ ...previous, curriculumStrand: event.target.value }))}
                          placeholder="Strand"
                          style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem' }}
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                        Sub-strand
                        <input
                          type="text"
                          value={formData.curriculumSubStrand}
                          onChange={(event) => setFormData((previous) => ({ ...previous, curriculumSubStrand: event.target.value }))}
                          placeholder="Sub-strand"
                          style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem' }}
                        />
                      </label>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr', gap: 12 }}>
                      <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                        Level Band
                        <input
                          type="text"
                          value={formData.curriculumLevelBand}
                          onChange={(event) => setFormData((previous) => ({ ...previous, curriculumLevelBand: event.target.value }))}
                          placeholder="e.g. Levels 5 and 6"
                          style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem' }}
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                        Pathway
                        <input
                          type="text"
                          value={formData.curriculumPathway}
                          onChange={(event) => setFormData((previous) => ({ ...previous, curriculumPathway: event.target.value }))}
                          placeholder="Pathway"
                          style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem' }}
                        />
                      </label>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr', gap: 12 }}>
                      <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                        Learning Area / Capability
                        <input
                          type="text"
                          value={formData.curriculumLearningAreaCapability}
                          onChange={(event) => setFormData((previous) => ({ ...previous, curriculumLearningAreaCapability: event.target.value }))}
                          placeholder="Learning area or capability"
                          style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem' }}
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                        Discipline / Language
                        <input
                          type="text"
                          value={formData.curriculumDisciplineLanguage}
                          onChange={(event) => setFormData((previous) => ({ ...previous, curriculumDisciplineLanguage: event.target.value }))}
                          placeholder="Discipline or language"
                          style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem' }}
                        />
                      </label>
                    </div>

                    <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                      Achievement Standard
                      <textarea
                        value={formData.curriculumAchievementStandard}
                        onChange={(event) => setFormData((previous) => ({ ...previous, curriculumAchievementStandard: event.target.value }))}
                        placeholder="Achievement standard"
                        rows={3}
                        style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', resize: 'vertical' }}
                      />
                    </label>

                    <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
                      Elaboration
                      <textarea
                        value={formData.curriculumElaboration}
                        onChange={(event) => setFormData((previous) => ({ ...previous, curriculumElaboration: event.target.value }))}
                        placeholder="Elaboration"
                        rows={3}
                        style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', resize: 'vertical' }}
                      />
                    </label>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button type="button" className="amd-btn amd-btn-primary" onClick={() => void handleSave()} disabled={saving}>
                      {saving ? 'Saving...' : isAdding ? 'Create category' : 'Save changes'}
                    </button>
                    <button type="button" className="amd-btn amd-btn-secondary" onClick={handleCancel}>
                      Cancel
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <AdminAiAssistantModal
        open={aiModalOpen}
        title={aiModalMode === 'single' ? 'Category AI Assistant (single)' : 'Category AI Assistant (bulk)'}
        promptLabel="What would you like the assistant to help with?"
        promptPlaceholder={aiModalMode === 'single'
          ? 'Example: write a polished category description grounded in the curriculum metadata.'
          : 'Example: generate 6 category drafts for selective-school maths topics in JSON.'}
        defaultPrompt={aiModalMode === 'single'
          ? `Create polished category content for category ID ${formData.id ?? 'new'} (${formData.name || 'this category'}). Use the curriculum metadata as the primary grounding source when present and do not invent conflicting codes, strands, or level bands. Provide the detailed description, tips, example, word detailed description, and tags for this specific category. Return structured JSON only with these keys: name, description, DetailedDescription, Tips, Example, WordDetailedDescription, Tags, and sortOrder.`
          : 'Generate a JSON array of 6 category drafts for a selective-school maths course. For each item include name, description, DetailedDescription, Tips, Example, WordDetailedDescription, Tags, and sortOrder. Make DetailedDescription rich HTML and descriptive for the course page. Make WordDetailedDescription book-focused and suitable for writing the book. Return valid JSON only.'}
        contextLabel="Category context"
        contextPlaceholder={aiModalMode === 'single'
          ? 'Include the category name, parent category, audience, curriculum metadata, and any existing text.'
          : 'Include the course focus, the audience, and any existing categories to avoid duplicates.'}
        initialContext={aiModalMode === 'single'
          ? buildSingleAiCurriculumContext()
          : `Course focus: ${courses.length ? courses.map((course) => course.name).slice(0, 3).join(', ') : 'general'}\nExisting category names: ${categories.slice(0, 8).map((category) => category.name).join(', ') || 'none'}`}
        promptKey={aiModalMode === 'single' ? 'admin-ai-assistant' : 'admin-categories-bulk-ai'}
        courseOptions={aiModalMode === 'bulk' ? courses.map((course) => ({ value: course.name, label: course.name })) : undefined}
        selectedCourseValue={aiModalMode === 'bulk' ? selectedBulkCourseName : ''}
        onCourseSelectionChange={aiModalMode === 'bulk' ? (value) => setSelectedBulkCourseName(value) : undefined}
        courseSelectorLabel="Target course"
        courseSelectorPlaceholder="Select a course"
        requireCourseSelection={aiModalMode === 'bulk'}
        applyLabel={aiModalMode === 'single' ? 'Apply to detailed description' : 'Review category drafts'}
        onClose={() => setAiModalOpen(false)}
        onApply={aiModalMode === 'single'
          ? (generatedText) => {
              const parsed = parseSingleCategoryAiResponse(generatedText);

              if (!parsed) {
                setFormData((previous) => ({ ...previous, detailedDescription: generatedText.trim() }));
                setMessage('AI response was not valid JSON. Applied the response to detailed description only.');
                return;
              }

              setFormData((previous) => ({
                ...previous,
                detailedDescription: toTrimmedString(parsed.DetailedDescription) ?? previous.detailedDescription,
                tips: toTrimmedString(parsed.Tips) ?? previous.tips,
                example: toTrimmedString(parsed.Example) ?? previous.example,
                wordDetailedDescription: toTrimmedString(parsed.WordDetailedDescription) ?? previous.wordDetailedDescription,
                tags: toTrimmedString(parsed.Tags) ?? previous.tags,
              }));
              setMessage('AI-generated content fields applied. Name and description were left unchanged.');
            }
          : handleGenerateCategoryDrafts}
      />

      {draftReviewOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(12, 24, 42, 0.68)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 1250,
          }}
          role="presentation"
          onClick={() => setDraftReviewOpen(false)}
        >
          <div
            style={{
              width: 'min(1000px, 100%)',
              maxHeight: '90vh',
              overflowY: 'auto',
              borderRadius: 16,
              background: '#fff',
              boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)',
              padding: 20,
              display: 'grid',
              gap: 12,
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Review AI category drafts"
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <h3 style={{ margin: 0, color: '#142541' }}>Review AI category drafts</h3>
                <div style={{ fontSize: '0.82rem', color: '#5f6f89', marginTop: 4 }}>
                  Confirm the draft list before creating the categories.
                </div>
              </div>
              <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setDraftReviewOpen(false)}>
                Close
              </button>
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              {draftReviewItems.map((draft) => (
                <div key={draft.id} style={{ border: '1px solid #e3ebf5', borderRadius: 12, padding: 12, background: '#f8fbff' }}>
                  <div style={{ fontWeight: 700, color: '#142541', marginBottom: 6 }}>{draft.name}</div>
                  <div style={{ fontSize: '0.84rem', color: '#51627c', marginBottom: 8 }}>{draft.description}</div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7a93' }}>
                    Sort order: {draft.sortOrder} • In questions: {draft.includeInTasks ?? false ? 'Yes' : 'No'} • Status: {draft.isEnabled ?? false ? 'Enabled' : 'Disabled'}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setDraftReviewOpen(false)}>
                Cancel
              </button>
              <button type="button" className="amd-btn amd-btn-primary" onClick={() => void handleApplyDrafts()} disabled={draftReviewLoading}>
                {draftReviewLoading ? 'Creating categories…' : `Create ${draftReviewItems.length} categories`}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        onClose={() => setConfirmDialog(null)}
        actions={confirmDialog?.actions || []}
      />

      {bulkUpdateOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(12, 24, 42, 0.68)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 1250,
          }}
          role="presentation"
          onClick={() => !bulkUpdateSaving && setBulkUpdateOpen(false)}
        >
          <div
            style={{
              width: 'min(560px, 100%)',
              borderRadius: 16,
              background: '#fff',
              boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)',
              padding: 18,
              display: 'grid',
              gap: 12,
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Bulk update categories"
            onClick={(event) => event.stopPropagation()}
          >
            <div>
              <h3 style={{ margin: 0, color: '#142541' }}>Bulk update categories</h3>
              <div style={{ fontSize: '0.82rem', color: '#5f6f89', marginTop: 4 }}>
                Apply quick updates to {selectedIds.length} selected categories.
              </div>
            </div>

            <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
              Status
              <select
                value={bulkUpdateDraft.status}
                onChange={(event) => setBulkUpdateDraft((previous) => ({ ...previous, status: event.target.value as BulkUpdateDraft['status'] }))}
                style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', background: '#fff' }}
              >
                <option value="no-change">No change</option>
                <option value="active">Active</option>
                <option value="not-active">Not Active</option>
              </select>
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
              Approval status
              <select
                value={bulkUpdateDraft.approvalStatus}
                onChange={(event) => setBulkUpdateDraft((previous) => ({ ...previous, approvalStatus: event.target.value as BulkUpdateDraft['approvalStatus'] }))}
                style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', background: '#fff' }}
              >
                <option value="no-change">No change</option>
                <option value="Pending Review">Pending Review</option>
                <option value="Approved">Approved</option>
                <option value="Manual">Manual</option>
              </select>
            </label>

            <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', color: '#243b69', fontWeight: 700 }}>
              In questions
              <select
                value={bulkUpdateDraft.includeInTasks}
                onChange={(event) => setBulkUpdateDraft((previous) => ({ ...previous, includeInTasks: event.target.value as BulkUpdateDraft['includeInTasks'] }))}
                style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', background: '#fff' }}
              >
                <option value="no-change">No change</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setBulkUpdateOpen(false)} disabled={bulkUpdateSaving}>
                Cancel
              </button>
              <button type="button" className="amd-btn amd-btn-primary" onClick={() => void handleBulkUpdate()} disabled={bulkUpdateSaving}>
                {bulkUpdateSaving ? 'Updating…' : 'Apply bulk update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(12, 24, 42, 0.68)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 1260,
          }}
          role="presentation"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            style={{
              width: 'min(980px, 100%)',
              maxHeight: '90vh',
              overflowY: 'auto',
              borderRadius: 16,
              background: '#fff',
              boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Category preview"
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ background: 'linear-gradient(to right, #2563eb, #4f46e5)', padding: 16, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div>
                <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>Category Preview</div>
                <h3 style={{ margin: '2px 0 0 0' }}>{previewCategory?.name || 'Loading...'}</h3>
              </div>
              <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setPreviewOpen(false)}>
                Close
              </button>
            </div>

            <div style={{ padding: 16, display: 'grid', gap: 16 }}>
              {previewLoading ? (
                <div style={{ minHeight: 220, display: 'grid', placeContent: 'center', color: '#4f658f', fontWeight: 700 }}>
                  Loading preview...
                </div>
              ) : previewCategory ? (
                <>
                  {previewCategory.description?.trim() && (
                    <div style={{ borderLeft: '4px solid #60a5fa', paddingLeft: 12 }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#0f172a' }}>Overview</h4>
                      <div className="html-content" style={{ color: '#374151', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: previewCategory.description }} />
                    </div>
                  )}

                  {previewCategory.detailedDescription?.trim() && (
                    <div style={{ borderLeft: '4px solid #818cf8', paddingLeft: 12 }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#0f172a' }}>Details</h4>
                      <div className="html-content" style={{ color: '#374151', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: previewCategory.detailedDescription }} />
                    </div>
                  )}

                  {previewCategory.example?.trim() && (
                    <div style={{ border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 10, padding: 12 }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#14532d' }}>Examples</h4>
                      <div className="html-content" style={{ color: '#14532d', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: previewCategory.example }} />
                    </div>
                  )}

                  {previewCategory.tips?.trim() && (
                    <div style={{ border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 10, padding: 12 }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#92400e' }}>Tips</h4>
                      <div className="html-content" style={{ color: '#92400e', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: previewCategory.tips }} />
                    </div>
                  )}
                </>
              ) : (
                <div style={{ minHeight: 220, display: 'grid', placeContent: 'center', color: '#4f658f' }}>
                  No preview content available.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCategoriesV2;
